import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Edit, MessageSquare, Users, CheckCircle, Clock, Loader2, Eye, Upload, Download, Crown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { getDifficultyLabel } from '../../utils/constants';

const goalLabels = {
  weight_loss: 'Weight Loss',
  muscle_building: 'Muscle Building',
  endurance: 'Endurance',
  general_fitness: 'General Fitness',
  strength: 'Strength',
  core_strength: 'Core Strength'
};

export const AdminUsersTab = ({ 
  users, 
  totalUsers, 
  loadingMoreUsers, 
  onLoadMoreUsers, 
  onLoadAllUsers, 
  onEditUser, 
  onMessageUser,
  onViewProfile,
  onTogglePremium,
  onBulkImport,
  onBulkImportFollows,
  onExportUsers,
  onExportFollows
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-card border border-border"
    >
      <div className="p-3 md:p-4 border-b border-border flex flex-col md:flex-row gap-3 md:gap-4 md:items-center md:justify-between">
        <div className="relative w-full md:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-black border-border"
            data-testid="admin-user-search"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <span className="text-muted-foreground text-xs md:text-sm w-full md:w-auto">
            Showing <span className="text-primary font-heading">{users.length}</span> of <span className="text-white font-heading">{totalUsers}</span>
          </span>
          {onBulkImport && (
            <Button
              onClick={onBulkImport}
              variant="outline"
              size="sm"
              className="border-border text-white hover:bg-white/5 h-8 px-2.5 text-xs md:h-9 md:px-3 md:text-sm"
              data-testid="bulk-import-users-btn"
            >
              <Upload className="h-3.5 w-3.5 mr-1 md:h-4 md:w-4 md:mr-2" />
              <span className="hidden md:inline">Import Users</span>
              <span className="md:hidden">Import</span>
            </Button>
          )}
          {onExportUsers && (
            <Button
              onClick={onExportUsers}
              variant="outline"
              size="sm"
              className="border-border text-white hover:bg-white/5 h-8 px-2.5 text-xs md:h-9 md:px-3 md:text-sm"
              data-testid="export-users-btn"
            >
              <Download className="h-3.5 w-3.5 mr-1 md:h-4 md:w-4 md:mr-2" />
              <span className="hidden md:inline">Export Users</span>
              <span className="md:hidden">Export</span>
            </Button>
          )}
          {onBulkImportFollows && (
            <Button
              onClick={onBulkImportFollows}
              variant="outline"
              size="sm"
              className="border-border text-white hover:bg-white/5 h-8 px-2.5 text-xs md:h-9 md:px-3 md:text-sm"
              data-testid="bulk-import-follows-btn"
            >
              <Upload className="h-3.5 w-3.5 mr-1 md:h-4 md:w-4 md:mr-2" />
              Follows
            </Button>
          )}
          {onExportFollows && (
            <Button
              onClick={onExportFollows}
              variant="outline"
              size="sm"
              className="border-border text-white hover:bg-white/5 h-8 px-2.5 text-xs md:h-9 md:px-3 md:text-sm"
              data-testid="export-follows-btn"
            >
              <Download className="h-3.5 w-3.5 mr-1 md:h-4 md:w-4 md:mr-2" />
              <span className="hidden md:inline">Export Follows</span>
              <span className="md:hidden">Exp. F</span>
            </Button>
          )}
          {users.length < totalUsers && (
            <Button
              onClick={onLoadAllUsers}
              variant="outline"
              size="sm"
              className="border-primary text-primary hover:bg-primary/10 h-8 px-2.5 text-xs md:h-9 md:px-3 md:text-sm"
              disabled={loadingMoreUsers}
              data-testid="load-all-users-btn"
            >
              {loadingMoreUsers ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1 md:h-4 md:w-4 md:mr-2" />
              ) : (
                <Users className="h-3.5 w-3.5 mr-1 md:h-4 md:w-4 md:mr-2" />
              )}
              <span className="hidden md:inline">Load All Members</span>
              <span className="md:hidden">Load All</span>
            </Button>
          )}
        </div>
      </div>

      {/* Mobile card list — hidden on md+ */}
      <div className="md:hidden divide-y divide-border">
        {filteredUsers.map((u, i) => (
          <div key={u.id} className="p-3" data-testid={`user-card-${i}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <p className="text-white font-medium text-sm truncate">{u.name}</p>
                <p className="text-muted-foreground text-xs truncate">{u.email}</p>
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <Button
                  onClick={() => onViewProfile?.(u)}
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 hover:bg-green-500/10"
                  data-testid={`view-user-${i}`}
                  title="View Profile"
                >
                  <Eye className="h-3.5 w-3.5 text-green-400" />
                </Button>
                <Button
                  onClick={() => onEditUser(u)}
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 hover:bg-primary/10"
                  data-testid={`edit-user-${i}`}
                  title="Edit User"
                >
                  <Edit className="h-3.5 w-3.5 text-primary" />
                </Button>
                <Button
                  onClick={() => onMessageUser(u)}
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 hover:bg-blue-500/10"
                  data-testid={`message-user-${i}`}
                  title="Send Message"
                >
                  <MessageSquare className="h-3.5 w-3.5 text-blue-400" />
                </Button>
                {onTogglePremium && (
                  <Button
                    onClick={() => onTogglePremium(u)}
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 hover:bg-yellow-500/10"
                    data-testid={`toggle-premium-${i}`}
                    title={u.is_premium ? 'Revoke Premium' : 'Grant Premium'}
                  >
                    <Crown className={`h-3.5 w-3.5 ${u.is_premium ? 'text-yellow-400' : 'text-muted-foreground'}`} />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {u.onboarding_completed ? (
                <Badge className="bg-green-500/20 text-green-400 text-[10px] px-1.5 py-0">
                  <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                  Active
                </Badge>
              ) : (
                <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px] px-1.5 py-0">
                  <Clock className="h-2.5 w-2.5 mr-0.5" />
                  Onboarding
                </Badge>
              )}
              {u.is_premium && (
                <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/40 text-[10px] px-1.5 py-0">
                  <Crown className="h-2.5 w-2.5 mr-0.5" />
                  Premium
                </Badge>
              )}
              {(u.fitness_goals || []).slice(0, 2).map(g => (
                <Badge key={g} className="bg-primary/20 text-primary text-[10px] px-1.5 py-0">
                  {goalLabels[g]?.split(' ')[0] || g}
                </Badge>
              ))}
              {(u.fitness_goals || []).length > 2 && (
                <Badge className="bg-white/10 text-white text-[10px] px-1.5 py-0">
                  +{u.fitness_goals.length - 2}
                </Badge>
              )}
              <span className="ml-auto text-muted-foreground text-[10px]">
                {new Date(u.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table — hidden on mobile */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-black">
            <tr>
              <th className="text-left p-4 text-muted-foreground font-heading uppercase text-sm">User</th>
              <th className="text-left p-4 text-muted-foreground font-heading uppercase text-sm">Goals</th>
              <th className="text-left p-4 text-muted-foreground font-heading uppercase text-sm">Level</th>
              <th className="text-left p-4 text-muted-foreground font-heading uppercase text-sm">Joined</th>
              <th className="text-left p-4 text-muted-foreground font-heading uppercase text-sm">Status</th>
              <th className="text-left p-4 text-muted-foreground font-heading uppercase text-sm">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u, i) => (
              <tr key={u.id} className="border-t border-border hover:bg-white/5">
                <td className="p-4">
                  <div>
                    <p className="text-white font-medium">{u.name}</p>
                    <p className="text-muted-foreground text-sm">{u.email}</p>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-1">
                    {(u.fitness_goals || []).slice(0, 2).map(g => (
                      <Badge key={g} className="bg-primary/20 text-primary text-xs">
                        {goalLabels[g]?.split(' ')[0] || g}
                      </Badge>
                    ))}
                    {(u.fitness_goals || []).length > 2 && (
                      <Badge className="bg-white/10 text-white text-xs">
                        +{u.fitness_goals.length - 2}
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-white capitalize">{getDifficultyLabel(u.experience_level) || '-'}</span>
                </td>
                <td className="p-4">
                  <span className="text-muted-foreground text-sm">
                    {new Date(u.created_at).toLocaleDateString()}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {u.onboarding_completed ? (
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-500/20 text-yellow-400">
                        <Clock className="h-3 w-3 mr-1" />
                        Onboarding
                      </Badge>
                    )}
                    {u.is_premium && (
                      <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/40">
                        <Crown className="h-3 w-3 mr-1" />
                        Premium
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => onViewProfile?.(u)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-green-500/10"
                      data-testid={`view-user-${i}`}
                      title="View Profile"
                    >
                      <Eye className="h-4 w-4 text-green-400" />
                    </Button>
                    <Button
                      onClick={() => onEditUser(u)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-primary/10"
                      data-testid={`edit-user-${i}`}
                      title="Edit User"
                    >
                      <Edit className="h-4 w-4 text-primary" />
                    </Button>
                    <Button
                      onClick={() => onMessageUser(u)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-blue-500/10"
                      data-testid={`message-user-${i}`}
                      title="Send Message"
                    >
                      <MessageSquare className="h-4 w-4 text-blue-400" />
                    </Button>
                    {onTogglePremium && (
                      <Button
                        onClick={() => onTogglePremium(u)}
                        variant="ghost"
                        size="sm"
                        className={`h-8 w-8 p-0 ${u.is_premium ? 'hover:bg-yellow-500/10' : 'hover:bg-yellow-500/20'}`}
                        data-testid={`toggle-premium-${i}`}
                        title={u.is_premium ? 'Revoke Premium' : 'Grant Premium'}
                      >
                        <Crown className={`h-4 w-4 ${u.is_premium ? 'text-yellow-400' : 'text-muted-foreground'}`} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Load More Button */}
      {users.length < totalUsers && (
        <div className="p-4 border-t border-border text-center">
          <Button
            onClick={onLoadMoreUsers}
            variant="outline"
            className="border-border text-white hover:bg-white/5"
            disabled={loadingMoreUsers}
            data-testid="load-more-users-btn"
          >
            {loadingMoreUsers ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Load More ({totalUsers - users.length} remaining)
          </Button>
        </div>
      )}
    </motion.div>
  );
};
